import { apiFetch } from './client';
import type { TopbarOrg } from '@/stores/uiStore';

/**
 * Session API — tracks topbar selectors (org, mode, persona) server-side
 * so user context persists across tabs. SPEC-FRONTEND_V4 §4.
 *
 * Backend endpoints live under /api/v2/auth/session* (auth-svc).
 */

export interface SessionContext {
  org: TopbarOrg;
  mode: string;
  personaId: string;
  activeFunction?: string | null;
  userId?: string;
  email?: string;
  isPlatformAdmin?: boolean;
}

export interface SessionUpdate {
  org?: TopbarOrg;
  mode?: string;
  personaId?: string;
  activeFunction?: string | null;
}

interface SessionEnvelope {
  session: {
    orgId?: string;
    org?: string;
    org_type?: string;
    mode?: string;
    persona?: string;
    activeFunction?: string | null;
    userId?: string;
    email?: string;
    roles?: string[];
  };
}

function toContext(env: SessionEnvelope): SessionContext {
  const s = env.session ?? {};
  return {
    org: (s.orgId || s.org || 'default') as TopbarOrg,
    mode: s.mode || 'all-modes',
    personaId: s.persona || 'dot-executive',
    activeFunction: s.activeFunction ?? null,
    userId: s.userId,
    email: s.email,
    isPlatformAdmin: Array.isArray(s.roles) && s.roles.includes('platform_admin'),
  };
}

export async function initSession(): Promise<SessionContext> {
  const env = await apiFetch<SessionEnvelope>('/api/v2/auth/session/init', {
    method: 'POST',
    body: JSON.stringify({}),
  });
  return toContext(env);
}

export async function updateSession(
  update: SessionUpdate,
): Promise<SessionContext> {
  const body: Record<string, unknown> = {};
  if (update.org !== undefined) body.orgId = update.org;
  if (update.mode !== undefined) body.mode = update.mode;
  if (update.personaId !== undefined) body.persona = update.personaId;
  if (update.activeFunction !== undefined) body.activeFunction = update.activeFunction;

  const env = await apiFetch<SessionEnvelope>('/api/v2/auth/session/update', {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
  return toContext(env);
}
